import React, { useState, useEffect } from 'react'
import { GoogleLogin } from '@react-oauth/google'
import { useNavigation } from '../shared/hooks/useNavigation'
import { ROUTES } from '../shared/navigation'
import { Mail, Lock, Eye, EyeOff, Phone } from '../components/icons'
import { useAuthStore } from '../stores/authStore'
import { authService } from '../services/authService'
import { companyService } from '../services/companyService'
import { runAuthTests } from '../utils/authTestUtils'
import CompleteProfileModal from '../components/CompleteProfileModal'

const LoginPage = () => {
  const { navigate } = useNavigation()
  const { login, isLoading, setLoading } = useAuthStore()

  const [showPassword, setShowPassword] = useState(false)
  const [emailOrPhone, setEmailOrPhone] = useState('')
  const [password, setPassword] = useState('')
  const [companyName, setCompanyName] = useState('Nome do Salão')
  const [error, setError] = useState('')
  const [showCompleteProfile, setShowCompleteProfile] = useState(false)
  const [pendingToken, setPendingToken] = useState(null)

  // Fetch company information when component mounts
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const companyInfo = await companyService.getCompanyInfo()
        if (companyInfo.name) {
          setCompanyName(companyInfo.name)
        }
      } catch (error) {
        // Ignore company info fetch errors - use default name
      }
    }

    fetchCompanyInfo()
    
    // Run authentication tests in development
    if (import.meta.env.DEV) {
      setTimeout(() => {
        runAuthTests()
      }, 1000)
    }
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    
    if (!emailOrPhone || !password) {
      setError('Por favor, preencha todos os campos.')
      return
    }

    setError('')
    setLoading(true)
    
    try {
      const tokenData = await authService.login(emailOrPhone, password)

      if (tokenData) {
        await login(tokenData)
        // Always redirect to client dashboard
        navigate(ROUTES.DASHBOARD)
      } else {
        setError('Credenciais inválidas. Tente novamente.')
      }
    } catch (error) {
      setError(error.message || 'Ocorreu um erro desconhecido.')
    } finally {
      setLoading(false)
    }
  }

  // Google OAuth success handler (popup mode)
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('')
    setLoading(true)

    try {
      const result = await authService.loginWithGoogle(credentialResponse.credential)

      if (result) {
        if (result.needs_profile_completion) {
          setPendingToken(result)
          setShowCompleteProfile(true)
        } else {
          await login(result)
          navigate(ROUTES.DASHBOARD)
        }
      } else {
        setError('Erro ao autenticar com Google. Tente novamente.')
      }
    } catch (error) {
      setError(error.message || 'Erro ao autenticar com Google.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleError = () => {
    setError('Erro ao conectar com Google. Tente novamente.')
  }

  // Handle profile completion
  const handleProfileComplete = async (profileData) => {
    if (!pendingToken) return

    setLoading(true)
    try {
      // First, log in with the pending token to set auth headers
      await login(pendingToken)

      // Then complete the profile
      const result = await authService.completeProfile(profileData)

      if (result) {
        // Update with new token containing updated user info
        await login(result)
        setShowCompleteProfile(false)
        setPendingToken(null)
        navigate(ROUTES.DASHBOARD)
      } else {
        setError('Erro ao completar perfil. Tente novamente.')
      }
    } catch (error) {
      setError(error.message || 'Erro ao completar perfil.')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAccount = () => {
    navigate(ROUTES.REGISTER)
  }

  return (
    <div className="h-full flex flex-col bg-brand-dark dark:bg-brand-light">
      {/* Header - Luxury Design */}
      <div className="safe-area-top bg-brand-dark dark:bg-brand-light px-6 py-8 text-center flex-shrink-0 border-b border-brand-light/10 dark:border-brand-dark/10">
        <h1 className="text-4xl font-serif font-bold text-brand-light dark:text-brand-dark mb-4 tracking-wide">
          {companyName}
        </h1>
        <div className="text-center">
          <p className="text-brand-light/70 dark:text-brand-dark/70 text-sm mb-2 tracking-wide uppercase">
            Acabou de baixar o app?
          </p>
          <button
            onClick={handleCreateAccount}
            className="text-brand-light dark:text-brand-dark text-sm font-medium uppercase tracking-widest border-b border-brand-light/30 dark:border-brand-dark/30 hover:border-brand-light dark:hover:border-brand-dark transition-all duration-300"
          >
            Criar conta
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-brand-light dark:bg-brand-dark px-6 py-8 overflow-y-auto overflow-x-hidden mobile-scroll min-h-0">
        {/* Header Text */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide uppercase">
            Entrar na sua conta
          </h2>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 border border-red-600/20 dark:border-red-400/20 bg-transparent">
            <p className="text-red-600 dark:text-red-400 text-sm text-center tracking-wide">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          {/* Email or Phone Field */}
          <div>
            <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              E-mail ou Telefone
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                {emailOrPhone.includes('@') ? (
                  <Mail size={18} strokeWidth={1.5} />
                ) : (
                  <Phone size={18} strokeWidth={1.5} />
                )}
              </div>
              <input
                type="text"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                placeholder="seu@email.com ou (11) 99999-9999"
                className="w-full pl-12 pr-4 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">
              Senha
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40">
                <Lock size={18} strokeWidth={1.5} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                className="w-full pl-12 pr-12 py-4 border border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide transition-all duration-300"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40 hover:text-brand-dark dark:hover:text-brand-light transition-all duration-300"
              >
                {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div className="text-right">
            <button
              type="button"
              className="text-brand-dark dark:text-brand-light text-xs uppercase tracking-wider border-b border-brand-dark/30 dark:border-brand-light/30 hover:border-brand-dark dark:hover:border-brand-light transition-all duration-300"
            >
              Esqueceu a senha?
            </button>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark text-sm font-medium border border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 disabled:bg-brand-dark/30 dark:disabled:bg-brand-light/30 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 uppercase tracking-widest"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-brand-light dark:border-brand-dark border-t-transparent animate-spin"></div>
            ) : (
              'Entrar'
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 h-px bg-brand-dark/10 dark:bg-brand-light/10"></div>
            <span className="px-4 text-brand-dark/60 dark:text-brand-light/60 text-xs uppercase tracking-wider">ou continue com</span>
            <div className="flex-1 h-px bg-brand-dark/10 dark:bg-brand-light/10"></div>
          </div>

          {/* Social Login Buttons */}
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="continue_with"
              shape="rectangular"
              width="100%"
            />
          </div>

          {/* Alternative Login Help */}
          <div className="text-center mt-8 pb-8">
            <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">
              Já tem uma conta?{' '}
              <span className="text-brand-dark dark:text-brand-light font-medium">
                Use suas credenciais acima
              </span>
            </p>
          </div>
        </form>
      </div>

      {/* Complete Profile Modal for new Google users */}
      <CompleteProfileModal
        isOpen={showCompleteProfile}
        onClose={() => {
          setShowCompleteProfile(false)
          setPendingToken(null)
        }}
        onSubmit={handleProfileComplete}
        isLoading={isLoading}
      />
    </div>
  )
}

export default LoginPage